import { useState } from "react";
import type { Deal } from "../types";
import { CHECKLIST, loadDone, saveDone, type ChecklistItem } from "../lib/checklist";
import { DataAndCrm } from "./DataAndCrm";

const CATS = ["Platform", "Data feeds — free", "AI & delivery", "Billing & payments", "Data feeds — paid", "Deal sourcing"];

export function Setup({ deals }: { deals: Deal[] }) {
  const [done, setDone] = useState<Record<string, boolean>>(loadDone);
  const toggle = (id: string) => {
    const next = { ...done, [id]: !done[id] };
    setDone(next);
    saveDone(next);
  };
  const total = CHECKLIST.length;
  const complete = CHECKLIST.filter((i) => done[i.id]).length;

  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ Your setup checklist</div>
        <p>
          Everything that makes Beacon go — ticked off as we wire it. The free platform + government data feeds are
          done; the paid feeds and delivery bits are one key each, whenever a deal justifies them. Below the list is your
          <b> deal-sourcing playbook</b>: where to find properties to run through Beacon.
        </p>
      </div>

      <DataAndCrm deals={deals} />

      {/* progress */}
      <div className="card pad" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div className="eyebrow">Setup progress</div>
          <div className="mono" style={{ color: "var(--forest)", fontWeight: 700 }}>{complete} / {total}</div>
        </div>
        <div className="bar" style={{ height: 10 }}><span style={{ width: (complete / total) * 100 + "%" }} /></div>
      </div>

      {CATS.map((cat) => {
        const items = CHECKLIST.filter((i) => i.category === cat);
        if (!items.length) return null;
        const isSourcing = cat === "Deal sourcing";
        return (
          <div key={cat} style={{ marginBottom: 16 }}>
            <h2 className="sechead" style={{ fontSize: 18 }}>{cat}{isSourcing ? " — where to find deals" : ""}</h2>
            {isSourcing && (
              <div className="note" style={{ marginBottom: 8 }}>
                The workflow: grab a candidate address from any source below → open a deal → <b>Data Sources → Auto-fill from address</b> →
                Beacon pulls the tract, rent ceiling, demographics &amp; flood, runs the four compliance gates, and the AI gives you a verdict. Shortlist the winners.
              </div>
            )}
            <div className="card pad">
              {items.map((it, idx) => (
                <Row key={it.id} it={it} done={!!done[it.id]} onToggle={() => toggle(it.id)} last={idx === items.length - 1} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Row({ it, done, onToggle, last }: { it: ChecklistItem; done: boolean; onToggle: () => void; last: boolean }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "11px 0", borderBottom: last ? "none" : "1px solid var(--line)" }}>
      <button
        onClick={onToggle}
        aria-label={done ? "Mark not done" : "Mark done"}
        style={{
          width: 22, height: 22, flexShrink: 0, marginTop: 1, borderRadius: 6, cursor: "pointer",
          border: "1.5px solid " + (done ? "var(--pass)" : "var(--line)"),
          background: done ? "var(--pass)" : "var(--white)", color: "#fff",
          display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700,
        }}
      >{done ? "✓" : ""}</button>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: done ? "var(--muted)" : "var(--forest)", textDecoration: done ? "line-through" : "none" }}>{it.label}</div>
        <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 2 }}>{it.desc}</div>
        {it.link && (
          <a href={it.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, color: "var(--brass-deep)", fontWeight: 600 }}>{it.linkLabel ?? "Open"} ↗</a>
        )}
      </div>
    </div>
  );
}
